/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
/* eslint-disable */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';

import AddDialogue from './AddDialogue';
import Notes from './Notes';

interface Props {
  token: string;
  lightMode: boolean;
}

const MAX_LEN = 100;

export default function Globals({ token, lightMode }: Props) {
  const [rowData, setRowData] = useState<{ id: string; name: string }[]>([]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const styles = lightMode ? lightStyles : darkStyles;

  // Initialize row data
  useEffect(() => {
    (async () => {
      const items = await (
        await fetch(`${process.env.REACT_APP_API_URL}/globals`, {
          method: 'GET',
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      ).json();
      setRowData(items || []);
    })();
  }, []);

  // Get selected rows from the grid
  const getSelectedRows = useCallback(() => {
    if (gridRef.current) {
      return gridRef.current.api.getSelectedRows();
    }
    return [];
  }, []);

  // Add popup
  const handleAddClick = useCallback(() => {
    setShowAddDropdown((prev) => !prev);
  }, []);

  // Add
  const handleAdd = useCallback(
    async (adds: { id: string; description: string }[]) => {
      try {
        setError(null);
        const addedRows: { id: string; name: string }[] = [];
        for (const add of adds) {
          const res = await (
            await fetch(`${process.env.REACT_APP_API_URL}/globals`, {
              method: 'POST',
              body: JSON.stringify({
                name: add.id,
              }),
              headers: {
                authorization: `Bearer ${token}`,
              },
            })
          ).json();
          addedRows.push({ id: res.id, name: add.id });
        }
        console.log(addedRows);
        setRowData((oldRowData) => [...oldRowData, ...addedRows]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add global status');
        console.error('Error adding global status:', err);
      }
    },
    [token],
  );

  // Delete
  const handleDelete = useCallback(async () => {
    try {
      setError(null);
      const selectedRows = getSelectedRows();
      if (selectedRows.length === 0) {
        setError('No rows selected for deletion');
        return;
      }
      const delIds: string[] = [];

      for (const row of selectedRows) {
        const res = await (
          await fetch(`${process.env.REACT_APP_API_URL}/globals/${row.id}`, {
            method: 'DELETE',
            headers: {
              authorization: `Bearer ${token}`,
            },
          })
        ).json();
        delIds.push(res.id);
      }
      console.log(delIds);
      setRowData((oldRowData) => oldRowData.filter((rowItem) => !delIds.includes(rowItem.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete global status');
      console.error('Error deleting global status:', err);
    }
  }, [getSelectedRows, token]);

  // Edit value
  const onCellValueChanged = useCallback(
    async (event: any) => {
      try {
        setError(null);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/globals/${event.data.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: event.data.name }),
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        
        if (!res.ok) {
          throw new Error(`Failed to update global status: ${res.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update global status');
        console.error('Error updating global status:', err);
      }
    },
    [token],
  );

  // Edit order
  const onRowDragEnd = useCallback(
    async (event: any) => {
      try {
        setError(null);
        if (!gridRef.current) return;

        // Get all row nodes in current order
        const rowNodes: any[] = [];
        gridRef.current.api.forEachNode((node) => rowNodes.push(node));

        // Extract the IDs in the new order
        const body = rowNodes.map((node) => node.data.id);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/globals/order`, {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
          },
        });
        
        if (!res.ok) {
          throw new Error(`Failed to update global status order: ${res.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update global status order');
        console.error('Error updating global status order:', err);
      }
    },
    [token],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: '',
        field: 'drag',
        suppressMenu: true,
        suppressSizeToFit: true,
        rowDrag: true,
        width: 50,
      },
      {
        headerName: '',
        field: 'selected',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMenu: true,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Name',
        field: 'name',
        editable: true,
        flex: 1,
        valueSetter: (params: { newValue: any; data: { name: any; }; }) => {
          const v = (params.newValue ?? "").toString();
          if (v.length > MAX_LEN || v.length < 1) {
            return false
          }
          params.data.name = v;
          return true;
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  return (
    <div className={styles.gridContainer}>
      {error && (
        <div className={styles.errorMessage} style={{ 
          color: 'red', 
          padding: '8px', 
          margin: '8px 0', 
          backgroundColor: 'rgba(255, 0, 0, 0.1)', 
          borderRadius: '4px',
          border: '1px solid red'
        }}>
          {error}
        </div>
      )}
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          <button type="button" className={`${styles.subTabButton} ${styles.subTabButtonActive}`}>
            Global Statuses
          </button>
        </div>
        <div className={styles.actionButtonsContainer}>
          <div className={styles.actionButtonsWrapper}>
            <button ref={addButtonRef} type="button" onClick={handleAddClick} className={styles.actionButton}>
              Add
            </button>
            {showAddDropdown && (
              <AddDialogue
                lightMode={lightMode}
                onAdd={handleAdd}
                onClose={() => setShowAddDropdown(false)}
                format='"&lt;Name&gt;"'
                example='"Out of Office"'
                params={1}
                validate={(codes) => {
                  for (const code of codes) {
                    if (code.id.length < 1) {
                      return `Name cannot be empty.`;
                    }
                    if (code.id.length > MAX_LEN) {
                      return `Name "${code.id}" must be ${MAX_LEN} characters or less.`;
                    }
                  }
                  return null;
                }}
              />
            )}
            <button type="button" onClick={handleDelete} className={styles.actionButton}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''}  ${styles.agThemeAlpineOverrides} ${
          styles.gridWrapper
        }`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
          rowDragManaged
          onRowDragEnd={onRowDragEnd}
          suppressMoveWhenRowDragging
        />
      </div>
      <Notes
        lightMode={lightMode}
        notes={`
            This page controls what statuses your agents can choose.
            These codes will be available upon page refresh for your agents inside
            of the profile page.
          `}
        // documents={[
        //   { name: 'Supporting Document', uri: 'https://google.ca' },
        //   { name: 'Instruction Manual', uri: 'https://yahoo.ca' },
        //   { name: 'Demo', uri: 'https://bing.com' },
        // ]}
      />
    </div>
  );
}
